// "You're Set Up" — final acknowledgment screen after the Get Set Up
// form is submitted. Includes the CC golfer logo + a video preview
// placeholder + the "What to expect at your fitting" CTA.
//
// Design comp reference: Screenshot 2026-05-25 6:19:06.

import { useState } from 'react';
import styles from '../styles/widget.module.css';
import { cancelBooking, ApiError } from '../api/client';
import type { BookingAction, BookingState } from '../state/machine';
import CLPImage from '../assets/images/clp-logo.png'

interface PostBookingCompleteProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
}

export function PostBookingComplete({ state, dispatch }: PostBookingCompleteProps) {
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);

  const handleCancel = async () => {
    if (!state.confirmation) return;
    if (!confirm('Cancel this booking? You can rebook anytime.')) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelBooking(state.confirmation.id, {
        email: state.email,
        phone: state.postBookingPhone.trim() || undefined,
      });
      setCancelled(true);
    } catch (err) {
      setCancelError(
        err instanceof ApiError
          ? `Cancel failed (${err.status}): ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Cancellation failed.',
      );
    } finally {
      setCancelling(false);
    }
  };

  if (cancelled) {
    return (
      <div className={styles.setUpRoot}>
        <h2 className={styles.setUpTitle}>Booking Cancelled</h2>
        <p className={styles.setUpSubtitle}>
          Your fitting at {state.selectedLocation?.name} has been cancelled.
        </p>
        <div className={styles.setUpCta}>
          <button className={styles.button} onClick={() => dispatch({ type: 'RESET' })}>
            Start a New Booking
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.setUpRoot}>
      <img src={CLPImage} className={styles.setUpImage} />
      <h2 className={styles.setUpTitle}>You&apos;re Set Up!</h2>
      <p className={styles.setUpSubtitle}>
        Your game is about to get better. Check your inbox ({state.email}).
      </p>
      <p className={styles.setUpBody}>
        Thanks for helping us ensure your fitting is successful! We&apos;ve sent your ball fitting
        preference and phone number over to your master fitter so they can gear up for your session.
        Keep an eye out for their pre-fit call soon to dial in your goals and kickstart your custom
        experience.
      </p>

      <div className={styles.setUpDual}>
        <div>
          <h3 className={styles.stepUpDualHeading}>
            Get Ready! Learn more about what a Club Champion fitting is like.
          </h3>
          <div className={styles.setUpVideo}>
            <span>▶ video preview</span>
          </div>
        </div>
      </div>

      {cancelError && <div className={styles.error} style={{ maxWidth: 700, margin: '16px auto 0' }}>{cancelError}</div>}

      <div className={styles.setUpCta}>
        <a
          href='https://clubchampion.com/golf-club-fitting-process'
          target='_blank'
          className={styles.button}
        >
          What to Expect at Your Fitting
        </a>
      </div>

      <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px dashed #ddd', textAlign: 'center', display: 'none' }}>
        <p className={styles.subtitle} style={{ fontSize: 11 }}>
          Need to cancel?
        </p>
        <button className={styles.linkButton} onClick={handleCancel} disabled={cancelling}>
          {cancelling ? 'Cancelling…' : 'Cancel this booking'}
        </button>
      </div>
    </div>
  );
}
